
import * as THREE from 'three'
import { XRButton } from 'three/addons/webxr/XRButton.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { FontLoader } from 'three/examples/jsm/loaders/FontLoader.js';
import { TextGeometry } from 'three/examples/jsm/geometries/TextGeometry.js';
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader.js';
import gsap from 'gsap';

// Configure GSAP for optimal performance with Three.js
gsap.ticker.lagSmoothing(0);
gsap.ticker.fps(60);
// GSAP will be driven manually via gsap.ticker.tick(delta) in the render loop

let renderer;
let pmrem; // PMREM generator for prefiltered environment maps
let lPinchOn = false;
let rPinchOn = false;
let baseComposition;
let productModel;
let delta;
let shoeColorLayer = null; // Reference to the shoe's color layer for dynamic color changes


// Rotation tracking variables
let leftHandRotationActive = false;
let rightHandRotationActive = false;
let lastRightHandX = 0;
let lastLeftHandX = 0;
let lastRightHandY = 0;
let lastLeftHandY = 0;

// Dynamic scaling parameters for dot planes
let planeScalingEnabled = true;
let planeBaseScale = 1.0;      // Base scale factor
let planeMinScale = 0.7;       // Minimum scale (farthest planes)
let planeMaxScale = 1.7;       // Maximum scale (closest planes)
let planeScaleRange = 0.1;     // Distance range for scaling effect

// Tag state tracking
const tagStates = new Array(12).fill(false); // false = OFF, true = ON
// Set specific tags to ON by default: Tag1, Tag3, Tag5, Tag7, Tag9, Tag11, Tag12
// Note: These are 0-indexed, so Tag1 = index 0, Tag3 = index 2, etc.
tagStates[0] = true;  // Tag1
tagStates[2] = true;  // Tag3
tagStates[4] = true;  // Tag5
tagStates[6] = true;  // Tag7
tagStates[8] = true;  // Tag9
tagStates[10] = true; // Tag11
tagStates[11] = true; // Tag12

// Cached references for performance optimization
const cachedDotPlanes = []; // Array to store references to all 12 dot planes
const cachedConnectionBoxes = []; // Array to store references to all 12 connection boxes

// Helper function to configure textures for optimal quality and performance
function configureTexture(texture) {
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.generateMipmaps = true;
    return texture;
}

// Size selection state tracking
let selectedSizeIndex = 2; // Default to text2 (9.5) - index 2
const sizeTexts = []; // Array to store references to all size text objects
let textConfigs = []; // Configuration for text objects
let selector = null; // Reference to selector object

// Size scale mapping for shoe model (text0-text4 corresponds to indices 0-4)
const sizeScales = [0.92, 0.97, 1.02, 1.06, 1.12]; // 92%, 97%, 102%, 106%, 112%

// Manual selector positions for each slot (these are the exact positions where selector should appear)
const selectorPositions = [
    { x: -0.052, y: -0.06, z: 0.021 }, // Slot 0: "8"
    { x: -0.002, y: -0.06, z: 0.021 }, // Slot 1: "9"
    { x: 0.052, y: -0.06, z: 0.021 },  // Slot 2: "9.5" (default)
    { x: 0.108, y: -0.06, z: 0.021 },  // Slot 3: "10"
    { x: 0.154, y: -0.06, z: 0.021 }   // Slot 4: "11"
];

// Function to update shoe color based on color picker
function updateShoeColor(color) {
    if (shoeColorLayer && shoeColorLayer.material) {
        // Update the color while preserving PBR properties
        shoeColorLayer.material.color.copy(color);
        shoeColorLayer.material.needsUpdate = true;
    }
}

// Size selection functions
function updateSizeSelection(newIndex) {
    try {
        if (!sizeTexts || !Array.isArray(sizeTexts)) {
            return;
        }
        
        // Reset all buttons to default state
        sizeTexts.forEach((textMesh, index) => {
            if (textMesh && textMesh.material) {
                try {
                    // Update material to default
                    textMesh.material.color.setHex(0xffffff);
                    textMesh.material.needsUpdate = true;
                    
                    // Scale to default
                    gsap.to(textMesh.scale, {
                        x: 1.0,
                        y: 1.0,
                        z: 1.0,
                        duration: 0.2,
                        ease: "power2.out"
                    });
                } catch (error) {
                }
            }
        });
        
        // Set new selected button to active state
        if (sizeTexts[newIndex] && sizeTexts[newIndex].material) {
            const selectedText = sizeTexts[newIndex];
            
            try {
                // Update material to active
                selectedText.material.color.setHex(0x00FF01);
                selectedText.material.needsUpdate = true;
                
                // Scale to active
                gsap.to(selectedText.scale, {
                    x: 1.2,
                    y: 1.2,
                    z: 1.2,
                    duration: 0.2,
                    ease: "power2.out"
                });
                
                // Update selector position using manual positions
                if (selector && selectorPositions[newIndex]) {
                    const targetPosition = selectorPositions[newIndex];
                    
                    
                    gsap.to(selector.position, {
                        x: targetPosition.x,
                        y: targetPosition.y,
                        z: targetPosition.z,
                        duration: 0.3,
                        ease: "power2.out"
                    });
                }
            } catch (error) {
            }
        }
        
        selectedSizeIndex = newIndex;
        
        // Update shoe model scale based on selected size
        if (productModel && sizeScales[newIndex] !== undefined) {
            const targetScale = sizeScales[newIndex];
            
            // Smooth transition to new scale
            gsap.to(productModel.scale, {
                x: targetScale,
                y: targetScale,
                z: targetScale,
                duration: 0.4,
                ease: "power2.out"
            });
            
        }
    } catch (error) {
    }
}

function animateSizeButtonPress(buttonIndex) {
    try {
        const button = sizeTexts[buttonIndex];
        if (!button || !button.userData || button.userData.isPressed) return;
        
        button.userData.isPressed = true;
        
        // Press animation: scale down to 0.6
        gsap.to(button.scale, {
            x: 0.6,
            y: 0.6,
            z: 0.6,
            duration: 0.1,
            ease: "power2.out",
            onComplete: () => {
                try {
                    // Update selection
                    updateSizeSelection(buttonIndex);
                    
                    // Reset press state
                    setTimeout(() => {
                        if (button && button.userData) {
                            button.userData.isPressed = false;
                        }
                    }, 100);
                } catch (error) {
                }
            }
        });
    } catch (error) {
    }
}

// Individual object rotation tracking
let shoeRotationX = 0;
let shoeRotationY = 0;
let cloudRotationX = 0;
let cloudRotationY = 0;
let rotationSpeed = 6.0;

// Grab interaction variables
let leftHandGrabbing = false;
let rightHandGrabbing = false;
let grabbedObject = null;
let originalParent = null;
let originalPosition = null;
let originalRotation = null;
let grabDistance = 0.15; // Distance threshold for grabbing objects (increased for easier grabbing)
let grabOffset = 0.05; // Offset distance to prevent hand-object intersection
let isAnimatingBack = false; // Flag to prevent multiple animations

// XR interaction gating to avoid auto-grab on session start
const XR_START_GRACE_MS = 1000;
let xrInteractionBlockedUntil = 0;
let userMovedShoeThisSession = false;
const SNAP_BACK_ON_RELEASE = true; // enable elastic return to center on release

// Pinch hysteresis (start < end) and priming to prevent phantom pinches
const PINCH_START = 0.018;
const PINCH_END = 0.028;
let leftPinchPrimed = false;
let rightPinchPrimed = false;

// Simple sanity check for tracked joints (avoid (0,0,0) / NaNs)
function isJointTracked(j) {
    return !!j && Number.isFinite(j.position?.x) && Number.isFinite(j.position?.y) && Number.isFinite(j.position?.z) && j.visible !== false;
}


// Button touch variables
let buttonTouchActive = false;
let lastButtonTouchTime = 0;
const buttonTouchCooldown = 500; // 500ms cooldown between touches

// Button state tracking
let isButtonPressed = false;
let colorCircleOpen = false; // Track open/close state of the color circle
let currentObjectType = 'shoe'; // Track current object: 'shoe' or 'cloud'

// Scene
const scene = new THREE.Scene()
const defaultBackground = new THREE.Color(0x505050);
scene.background = defaultBackground;

// Canvas
const canvas = document.querySelector('canvas.webgl')

// Camera
const camera = new THREE.PerspectiveCamera( 50, window.innerWidth / window.innerHeight, 0.1, 50 );
camera.position.set( 0, 1.6, 3 );
scene.add(camera)

// Renderer
renderer = new THREE.WebGLRenderer( { antialias: true } );
renderer.setPixelRatio( window.devicePixelRatio );
renderer.setSize( window.innerWidth, window.innerHeight );
renderer.xr.enabled = true;
// Enable physically correct lighting and tone mapping
renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = 1.0;
renderer.outputColorSpace = THREE.SRGBColorSpace;
renderer.physicallyCorrectLights = true;
// Disable shadows for XR/MR performance and visual quality
renderer.shadowMap.enabled = false;
document.body.appendChild( renderer.domElement );

// PMREM setup for environment prefiltering
pmrem = new THREE.PMREMGenerator(renderer);
pmrem.compileEquirectangularShader();

// Create WebXR button but keep it hidden; we'll trigger it from full-screen clicks
const xrButton = XRButton.createButton( renderer, { 'optionalFeatures': [ 'hand-tracking' ] } );
xrButton.style.display = 'none';
xrButton.style.opacity = '0';
xrButton.style.pointerEvents = 'none';
xrButton.setAttribute('aria-hidden', 'true');
document.body.appendChild( xrButton );

// Make entire screen clickable to start XR (matches ONLYREFERENCE pattern)
document.body.style.cursor = 'pointer';
const onBodyClickToEnterXR = () => {
    // forward to the hidden XR button to ensure proper feature policy & user gesture
    xrButton.click();
};
document.body.addEventListener('click', onBodyClickToEnterXR, { passive: true });

// Belt-and-suspenders: aggressively hide any default XR/VR/AR buttons injected by libs
function hideDefaultXRButtonsInTree(root) {
    if (!root) return;
    const candidates = root.querySelectorAll ? root.querySelectorAll('button, a, div, canvas, span') : [];
    const shouldHide = (el) => {
        if (!(el instanceof HTMLElement)) return false;
        const cls = (el.className || '').toString().toLowerCase();
        const id = (el.id || '').toString().toLowerCase();
        const txt = (el.textContent || '').toLowerCase();
        // Common identifiers or visible labels
        if (cls.includes('xr') || cls.includes('webxr') || cls.includes('vr')) return true;
        if (id.includes('xr') || id.includes('webxr') || id.includes('vr')) return true;
        if (/enter\s*(xr|ar|vr)/i.test(txt) || /start\s*(xr|ar|vr)/i.test(txt)) return true;
        return false;
    };
    candidates.forEach((el) => {
        if (shouldHide(el) && el !== xrButton) {
            el.style.display = 'none';
            el.style.opacity = '0';
            el.style.pointerEvents = 'none';
            el.setAttribute('aria-hidden', 'true');
        }
    });
}
// Initial sweep
hideDefaultXRButtonsInTree(document.body);
// Observe DOM for future inserts
const mo = new MutationObserver((mutations) => {
    for (const m of mutations) {
        if (m.type === 'childList') {
            m.addedNodes.forEach((n) => {
                if (n instanceof HTMLElement) {
                    hideDefaultXRButtonsInTree(n);
                }
            });
        }
    }
});
mo.observe(document.body, { childList: true, subtree: true });

// Toggle a CSS class on XR session start/end so we can style the page accordingly
renderer.xr.addEventListener('sessionstart', () => {
    document.body.classList.add('xr-presenting');
    // In XR, keep passthrough (transparent background)
    scene.background = null;
});
renderer.xr.addEventListener('sessionend', () => {
    document.body.classList.remove('xr-presenting');
    // Restore gray background when exiting XR
    scene.background = defaultBackground;
});

// Base Composition (container for the chair)
const tempGeo = new THREE.BoxGeometry(0.3, 0.3, 0.3);
const tempMat = new THREE.MeshBasicMaterial({ 
    color: 0xffffff, 
    transparent: true,
    opacity: 0.0,
    depthTest: false,
    depthWrite: false
});
baseComposition = new THREE.Mesh(tempGeo, tempMat);
baseComposition.scale.set(0.9, 0.9, 0.9); // Scaled to 90% of original size
scene.add(baseComposition);
// Hide all 3D content in 2D/desktop view by default
baseComposition.visible = false;

// Create a parent group for the cube and sphere that will handle rotation
const objectsParent = new THREE.Group();
objectsParent.position.set(0, 0, 0); // Position at center of base composition
objectsParent.rotation.set(0, 0, 0); // Ensure no initial rotation
baseComposition.add(objectsParent);

// Helper: fully reset any grab-related state and ensure product is correctly parented
function resetGrabAndPinchState() {
    // Clear pinch flags
    lPinchOn = false;
    rPinchOn = false;

    // Stop rotations driven by hands
    leftHandRotationActive = false;
    rightHandRotationActive = false;

    // If currently grabbed, restore to original hierarchy
    if (productModel) {
        const currentParent = productModel.parent;
        // If parented under a pinch sphere, move back to objectsParent
        if (currentParent === lPinchSphere || currentParent === rPinchSphere) {
            objectsParent.add(productModel);
            productModel.position.set(0, 0.03, 0);
            productModel.rotation.set(0, 0, 0);
            const currentScale = sizeScales[selectedSizeIndex] || 1.0;
            productModel.scale.set(currentScale, currentScale, currentScale);
        }
    }

    // Clear grab state bookkeeping
    leftHandGrabbing = false;
    rightHandGrabbing = false;
    grabbedObject = null;
    originalParent = null;
    originalPosition = null;
    originalRotation = null;
    isAnimatingBack = false;
}

// Helper: drop grabbed object in place by preserving world transform on reparent
function dropGrabbedObjectInPlace(obj, targetParent) {
    if (!obj || !targetParent) return;
    obj.updateMatrixWorld(true);
    const worldPos = new THREE.Vector3();
    const worldQuat = new THREE.Quaternion();
    obj.getWorldPosition(worldPos);
    obj.getWorldQuaternion(worldQuat);

    targetParent.add(obj);

    // Convert world -> local
    targetParent.worldToLocal(worldPos);
    const parentWorldQuat = targetParent.getWorldQuaternion(new THREE.Quaternion());
    const localQuat = worldQuat.clone().multiply(parentWorldQuat.invert());

    obj.position.copy(worldPos);
    obj.quaternion.copy(localQuat);
}

// Add ground plane as child of base composition
const planeGeometry = new THREE.PlaneGeometry(0.4, 0.4); // 200% of 0.2x0.2 = 0.4x0.4 units

// Load UI circle texture for the plane
const textureLoader = new THREE.TextureLoader();
const uiCircleTexture = configureTexture(textureLoader.load('/static/ui_circle.png'));

const planeMaterial = new THREE.MeshLambertMaterial({ 
    map: uiCircleTexture,
    transparent: true,
    opacity: 0.8, // Adjust opacity as needed
    side: THREE.DoubleSide,
    toneMapped: false
});
const groundPlane = new THREE.Mesh(planeGeometry, planeMaterial);

// Rotate the plane 90 degrees around X-axis to make it horizontal (ground)
groundPlane.rotation.x = -Math.PI / 2; // -90 degrees in radians

// Position the plane below the center of the base composition
groundPlane.position.set(0, -0.1, 0); // Moved down by -0.1 on Y-axis

// Add the ground plane as a child of the base composition
baseComposition.add(groundPlane);

// Add a second plane facing the player at the center
const buttonGeometry = new THREE.PlaneGeometry(0.1517, 0.0525); // 82% of 185x64 proportion (0.185x0.064)

// Load the button texture
const buttonTexture = configureTexture(textureLoader.load('/static/ui_button-search.png'));
const buttonTextureEdit = configureTexture(textureLoader.load('/static/ui_button-search.png'));
const buttonTextureDone = configureTexture(textureLoader.load('/static/ui_button-done.png'));

const buttonMaterial = new THREE.MeshBasicMaterial({ 
    map: buttonTexture, // Using search texture
    transparent: true,
    opacity: 1.0,
    side: THREE.DoubleSide,
    toneMapped: false
});

// Function to update button texture based on current object
function updateButtonTexture() {
    if (currentObjectType === 'shoe') {
        buttonMaterial.map = buttonTexture;
    } else {
        buttonMaterial.map = buttonTextureDone;
    }
    buttonMaterial.needsUpdate = true;
}

// Function to animate button press feedback with smoother transitions
function animateButtonPress() {
    if (isButtonPressed) return; // Prevent multiple animations
    
    isButtonPressed = true;
    
    // Store original scale
    const originalScale = 1.0;
    
    // Scale down with matching tag animation
    gsap.to(button.scale, {
        x: originalScale * 0.7,
        y: originalScale * 0.7,
        z: originalScale * 0.7,
        duration: 0.12,
        ease: "power2.out",
        onComplete: () => {
            // Change texture when at smallest scale
            toggleShoeCloudVisibility();
            
            // Scale up with matching tag animation
            gsap.to(button.scale, {
                x: originalScale,
                y: originalScale,
                z: originalScale,
                duration: 0.3,
                ease: "power2.out",
                onComplete: () => {
                    isButtonPressed = false;
                }
            });
        }
    });
}

const button = new THREE.Mesh(buttonGeometry, buttonMaterial);

// Position the plane at the specified coordinates
button.position.set(0, -0.18, 0.22); // Position: (0, -0.15, 0.22)
// Rotate 20 degrees up (around X-axis)
button.rotation.x = -20 * Math.PI / 180; // -20 degrees in radians (negative for upward rotation)

// Add the second plane as a child of the base composition
baseComposition.add(button);

// Add a new plane facing the user at the center, positioned higher up
// Calculate dimensions based on 406:201 aspect ratio
const headerWidth = 0.406 * 0.9; // Base width at 90% of original size
const headerHeight = headerWidth * (201 / 406); // Height based on aspect ratio
const headerGeometry = new THREE.PlaneGeometry(headerWidth, headerHeight);

// Load UI header texture for the plane
const headerTexture = configureTexture(textureLoader.load('/static/ui_header.png', 
    // Success callback
    function(texture) {
        headerMaterial.needsUpdate = true;
    },
    // Progress callback
    function(xhr) {
        // Progress loading
    },
    // Error callback
    function(error) {
    }
));

// Create a material for the header plane using the loaded texture
const headerMaterial = new THREE.MeshBasicMaterial({ 
    map: headerTexture,
    transparent: true,
    opacity: 1.0,
    side: THREE.DoubleSide,
    toneMapped: false
});

const header = new THREE.Mesh(headerGeometry, headerMaterial);

// Position the plane at the center but moved up
header.position.set(0, 0.34, 0); // Moved up by 0.34 on Y-axis

// Rotate the plane to face the user (camera-facing)
// No rotation needed as PlaneGeometry faces forward by default
// The plane will naturally face the user when positioned in front

// Add the header plane as a child of the base composition
baseComposition.add(header);

// Load Helvetica Black font and create size text
const fontLoader = new FontLoader();
fontLoader.load('/static/fonts/helvetica-black.json', function(font) {
    const textGeometry = new TextGeometry('9.5', {
        font: font,
        size: 0.02,  // Reduced size to 0.02 units
        height: 0.001,  // Very thin depth
        curveSegments: 12,
        bevelEnabled: false
    });

    // Create materials for size buttons (avoid cloning for better performance)
    const defaultTextMaterial = new THREE.MeshBasicMaterial({ 
        color: 0xffffff,  // White color for default state
        toneMapped: false
    });
    const activeTextMaterial = new THREE.MeshBasicMaterial({ 
        color: 0x00FF01,  // Green color for active state
        toneMapped: false
    });

    // Create text objects with radio button functionality
    textConfigs = [
        { text: '8', position: [-0.06, -0.07, 0.001], index: 0 },
        { text: '9', position: [-0.01, -0.07, 0.001], index: 1 },
        { text: '9.5', position: [0.032, -0.07, 0.001], index: 2 },
        { text: '10', position: [0.09, -0.07, 0.001], index: 3 },
        { text: '11', position: [0.137, -0.07, 0.001], index: 4 }
    ];
    
    // Create all text objects
    textConfigs.forEach(config => {
        const textGeometry = new TextGeometry(config.text, {
            font: font,
            size: 0.02,
            height: 0.001,
            curveSegments: 12,
            bevelEnabled: false
        });
        
        // Create individual material for each text to avoid shared state issues
        const textMaterial = new THREE.MeshBasicMaterial({ 
            color: 0xffffff,  // White color for default state
            toneMapped: false
        });
        const textMesh = new THREE.Mesh(textGeometry, textMaterial);
        textMesh.position.set(...config.position);
        
        // Store references for interaction
        textMesh.userData.isSizeButton = true;
        textMesh.userData.sizeIndex = config.index;
        textMesh.userData.isPressed = false;
        
        // Add to sizeTexts array for easy access
        sizeTexts[config.index] = textMesh;
        
        // Add to header
        header.add(textMesh);
    });
    
    // Set initial state (text2 "9.5" is selected by default)
    // Wait a frame to ensure all objects are created before setting initial selection
    setTimeout(() => {
        updateSizeSelection(selectedSizeIndex);
    }, 100);
    
    // Create selector plane
    const selectorGeometry = new THREE.PlaneGeometry(0.06, 0.06); // Square size 0.06
    const selectorTexture = configureTexture(textureLoader.load('/static/ui_selector.png', 
        // Success callback
        function(texture) {
            selectorMaterial.needsUpdate = true;
        },
        // Progress callback
        function(xhr) {
            // Progress loading
        },
        // Error callback
        function(error) {
        }
    ));

    const selectorMaterial = new THREE.MeshBasicMaterial({ 
        map: selectorTexture,
        transparent: true,
        opacity: 1.0,
        side: THREE.DoubleSide,
        toneMapped: false
    });

    selector = new THREE.Mesh(selectorGeometry, selectorMaterial);
    // Set initial position to match the default selected slot (9.5 at index 2)
    const initialPosition = selectorPositions[selectedSizeIndex];
    selector.position.set(initialPosition.x, initialPosition.y, initialPosition.z);
    header.add(selector);

});

// Create a small sphere as a visual indicator
const sphereGeometry = new THREE.SphereGeometry(0.025, 16, 16); // 0.025 units radius
const sphereMaterial = new THREE.MeshBasicMaterial({ color: 0xFF0000 }); // Starting with red (#FF0000)
const colorButton = new THREE.Mesh(sphereGeometry, sphereMaterial);

// Create a plane for the close button
const closeButtonGeometry = new THREE.PlaneGeometry(0.04, 0.04);
const closeButtonTexture = configureTexture(textureLoader.load('/static/ui_close.png'));
const closeButtonMaterial = new THREE.MeshBasicMaterial({
    map: closeButtonTexture,
    transparent: true,
    opacity: 1.0,
    side: THREE.DoubleSide,
    depthTest: false,
    depthWrite: false,
    toneMapped: false
});
const closeButtonPlane = new THREE.Mesh(closeButtonGeometry, closeButtonMaterial);
closeButtonPlane.position.set(0, 0, 0);
closeButtonPlane.scale.set(0, 0, 0); // Start with scale 0
colorButton.add(closeButtonPlane);

// Create an empty object to serve as a reference point/origin for the color circle
const colorCircleOrigin = new THREE.Object3D();

// Position both the sphere and the empty object at the same position
const colorPickerPosition = new THREE.Vector3(-0.15, -0.06, 0); // Down and left from center, on the plane
colorButton.position.copy(colorPickerPosition);
colorCircleOrigin.position.copy(colorPickerPosition);

// Set initial state to OFF (scale 0)
colorCircleOrigin.scale.set(0, 0, 0);

// Add state tracking for the color button
colorButton.userData.isPressed = false;

// Create a small square plane as a child of the sphere
const childPlaneSize = 0.2; // square size
const childPlaneGeometry = new THREE.PlaneGeometry(childPlaneSize, childPlaneSize);
// Load color circle texture
const colorCircleTexture = configureTexture(textureLoader.load('/static/ui_colorcircle.png'));
const childPlaneMaterial = new THREE.MeshBasicMaterial({ 
    map: colorCircleTexture,
    transparent: true,
    opacity: 1.0,
    side: THREE.DoubleSide,
    toneMapped: false
});
const colorCirclePlane = new THREE.Mesh(childPlaneGeometry, childPlaneMaterial);

// Position the plane centered on the origin but slightly forward to avoid z-fighting
colorCirclePlane.position.set(0, 0, 0.05);

// Create an empty object at the center of the color circle
const colorCircleCenter = new THREE.Object3D();
colorCircleCenter.position.set(0, 0, 0);
colorCirclePlane.add(colorCircleCenter);

// Add the plane as a child of the origin object
colorCircleOrigin.add(colorCirclePlane);

// Create a white dot that moves around the color circle
const colorCircleDot = new THREE.Mesh(
    new THREE.SphereGeometry(0.017, 16, 16),
    new THREE.MeshBasicMaterial({ color: 0xFF0000 }) // Starting with red (#FF0000)
);
// Create a small plane as a child of the dot
const dotPlaneGeometry = new THREE.CircleGeometry(0.0225, 32);
// Load dot circle texture
const dotCircleTexture = configureTexture(textureLoader.load('/static/ui_dotcircle.png'));
const dotPlaneMaterial = new THREE.MeshBasicMaterial({ 
    map: dotCircleTexture,
    transparent: true,
    opacity: 1.0,
    side: THREE.DoubleSide,
    depthTest: false,
    depthWrite: false,
    toneMapped: false
});
const dotPlane = new THREE.Mesh(dotPlaneGeometry, dotPlaneMaterial);
dotPlane.position.set(0, 0, 0.002);
colorCircleDot.add(dotPlane);

// Ensure dot and its plane render on top of colorCirclePlane
colorCircleDot.renderOrder = 1;
dotPlane.renderOrder = 1;

// Position it centered on the colorCirclePlane
colorCircleDot.position.set(0.081, 0, 0);
// Store the initial radius (distance from center)
colorCircleDot.userData.radius = 0.081; // Store initial radius for later use
colorCircleDot.userData.isDragging = false; // Track if being dragged
colorCircleDot.userData.activeHand = null; // Track which hand started the drag ('left' or 'right')
// Add the dot as a child of the colorCirclePlane
colorCirclePlane.add(colorCircleDot);

// Add both the sphere and the origin object as children of the header plane
header.add(colorButton);
header.add(colorCircleOrigin);

// Create a copy of the header plane for tags with new proportions (270x92)
const headerTagsWidth = 0.270 * 0.9; // Base width at 90% of original size
const headerTagsHeight = headerTagsWidth * (92 / 270); // Height based on new aspect ratio
const headerTagsGeometry = new THREE.PlaneGeometry(headerTagsWidth, headerTagsHeight);

// Load UI header-tags texture
const headerTagsTexture = configureTexture(textureLoader.load('/static/ui_header-tags.png', 
    // Success callback
    function(texture) {
        headerTagsMaterial.needsUpdate = true;
    },
    // Progress callback
    function(xhr) {
        // Progress loading
    },
    // Error callback
    function(error) {
    }
));

// Create a material for the header tags plane
const headerTagsMaterial = new THREE.MeshBasicMaterial({ 
    map: headerTagsTexture,
    transparent: true,
    opacity: 1.0,
    side: THREE.DoubleSide,
    toneMapped: false
});

const headerTags = new THREE.Mesh(headerTagsGeometry, headerTagsMaterial);

// Position at the same position as the original header
headerTags.position.set(0, 0.34, 0);
// Start with scale 0
headerTags.scale.set(0, 0, 0);

// Add the header tags plane to the base composition
baseComposition.add(headerTags);

// Auto-position composition at user's head height
let compositionPositioned = false;
let xrSessionStartTime = null;

function positionCompositionAtHeadHeight() {
    if (compositionPositioned || !xrSessionStartTime) return;
    
    // Wait for scene to settle and avoid bouncing
    const elapsedTime = Date.now() - xrSessionStartTime;
    if (elapsedTime < 1000) {
        return; // Wait for 1 second for scene to settle
    }
    
    // Get user's head position (camera position) - use a stable reference
    const headHeight = camera.position.y;
    const headDistance = camera.position.z;
    
    // Position composition at head height, slightly in front of user
    // Use direct position setting without animation to avoid bouncing
    baseComposition.position.set(0, headHeight - 0.2, headDistance - 1.0);
    
    // Ensure the shoe is at its correct initial position
    if (productModel) {
        productModel.position.set(0, 0.03, 0);
        productModel.rotation.set(0, 0, 0);
    }
    
    compositionPositioned = true;
}

// Listen for WebXR session start
renderer.xr.addEventListener('sessionstart', () => {
    xrSessionStartTime = Date.now();
    xrInteractionBlockedUntil = xrSessionStartTime + XR_START_GRACE_MS;
    leftPinchPrimed = false;
    rightPinchPrimed = false;
    compositionPositioned = false; // Reset so we can position again
    // Show 3D content only during active WebXR session
    if (baseComposition) baseComposition.visible = true;
    
    // Reset shoe position immediately when session starts
    if (productModel) {
        // Kill any existing GSAP animations
        gsap.killTweensOf(productModel.position);
        gsap.killTweensOf(productModel.rotation);
        gsap.killTweensOf(productModel.scale);
        
        // Set the shoe to its correct initial position if user hasn't moved it yet
        if (!userMovedShoeThisSession) {
            productModel.position.set(0, 0.03, 0);
            productModel.rotation.set(0, 0, 0);
            const currentScale = sizeScales[selectedSizeIndex] || 1.0;
            productModel.scale.set(currentScale, currentScale, currentScale);
        }
    }

    // Ensure we are not starting in a grabbed state
    resetGrabAndPinchState();
});

// Listen for WebXR session end
renderer.xr.addEventListener('sessionend', () => {
    xrSessionStartTime = null;
    xrInteractionBlockedUntil = 0;
    leftPinchPrimed = false;
    rightPinchPrimed = false;
    compositionPositioned = false; // Reset for next session
    // Hide 3D content when WebXR session ends
    if (baseComposition) baseComposition.visible = false;
});

// Load the shoe GLB file
const loader = new GLTFLoader();
loader.load('/static/shoe.glb', (gltf) => {
    productModel = gltf.scene;
    
    // Scale the shoe model based on selected size (default is index 2 = 102%)
    const initialScale = sizeScales[selectedSizeIndex] || 1.0;
    productModel.scale.set(initialScale, initialScale, initialScale);
    
    // Position the shoe in the center of the base composition
    productModel.position.set(0, 0.03, 0);
    
    // Rotate the shoe (adjust these values as needed)
    productModel.rotation.set(0, 0, 0); // X, Y, Z rotation in radians
    
    // Find and store reference to the "color" layer for dynamic color changes
    productModel.traverse((child) => {
        if (child.name === 'color' && child.isMesh) {
            shoeColorLayer = child; // Store reference for later use
            // Create realistic PBR material
            const shoeMaterial = new THREE.MeshPhysicalMaterial({
                color: 0xFF0000,       // your picker will keep updating this
                metalness: 0.02,       // leather is non-metal
                roughness: 0.55,       // matte but still catches light
                clearcoat: 0.08,       // very slight clearcoat for subtle spec highlights
                clearcoatRoughness: 0.6,
                sheen: 0.15,           // soft fabric-like lobe (subtle)
                sheenRoughness: 0.8,
                envMapIntensity: 0.9,  // pull reflections back a bit
                transparent: false,
                opacity: 1.0
            });
            child.material = shoeMaterial;
        }
    });
    
    // Add the shoe as a child of the objects parent group
    objectsParent.add(productModel);
    
    // Set initial visibility - shoe visible, cloud hidden
    productModel.visible = true;
    if (cloudModel) cloudModel.visible = false;
    
    // Ensure the shoe starts at the correct position without any animations
    if (productModel) {
        // Kill any existing GSAP animations on this object
        gsap.killTweensOf(productModel.position);
        gsap.killTweensOf(productModel.rotation);
        gsap.killTweensOf(productModel.scale);
        
        // Set the final position and scale immediately
        productModel.position.set(0, 0.03, 0);
        productModel.rotation.set(0, 0, 0);
        const currentScale = sizeScales[selectedSizeIndex] || 1.0;
        productModel.scale.set(currentScale, currentScale, currentScale);
    }
}, 
// Progress callback
(xhr) => {
    // Progress loading
},
// Error callback
(error) => {
    // Fallback to a simple cube if loading fails
    const cubeGeometry = new THREE.BoxGeometry(0.8, 0.8, 0.8);
    const cubeMaterial = new THREE.MeshStandardMaterial({ 
        color: 0x0066FF,
        metalness: 0.4,
        roughness: 0.35,
        envMapIntensity: 1.5,
        transparent: false,
        opacity: 1.0
    });
    productModel = new THREE.Mesh(cubeGeometry, cubeMaterial);
    const initialScale = sizeScales[selectedSizeIndex] || 1.0;
    productModel.scale.set(initialScale, initialScale, initialScale);
    productModel.position.set(0, 0.03, 0);
    productModel.rotation.set(0, 0, 0);
    objectsParent.add(productModel);
    productModel.visible = true;
    if (cloudModel) cloudModel.visible = false;
    
    // Set initial button texture to match shoe (edit button)
    currentObjectType = 'shoe';
    updateButtonTexture();
});



// Load the cloud GLB file
let cloudModel = null;
loader.load('/static/cloud.glb', (gltf) => {
    cloudModel = gltf.scene;
    
    // Scale the cloud model appropriately
    cloudModel.scale.set(0.205, 0.205, 0.205);
    
    // Position the cloud in the center of the base composition
    cloudModel.position.set(0, 0.03, 0);
    
    // Rotate the cloud (adjust these values as needed)
    cloudModel.rotation.set(0, 0, 0);
    
    // Add the cloud as a child of the objects parent group
    objectsParent.add(cloudModel);
    
            // Create a small white sphere at the center of the cloud
            const centerSphereGeometry = new THREE.SphereGeometry(0.02, 16, 16);
            const centerSphereMaterial = new THREE.MeshBasicMaterial({ 
                color: 0xffffff,
                transparent: true,
                opacity: 0.5
    });
    const centerSphere = new THREE.Mesh(centerSphereGeometry, centerSphereMaterial);
    centerSphere.scale.set(1, 1, 1); // The sphere will inherit the cloud's scale
    centerSphere.position.set(0, 0, 0); // Center of the cloud
    cloudModel.add(centerSphere);
    
    // Set initial visibility - cloud hidden initially
    cloudModel.visible = false;
    
    // Create and attach 12 small planes to the dot parts
    createDotPlanes(cloudModel);
    
    // Ensure the cloud starts at the correct position
    if (cloudModel) {
        // Kill any existing GSAP animations on this object
        gsap.killTweensOf(cloudModel.position);
        gsap.killTweensOf(cloudModel.rotation);
        gsap.killTweensOf(cloudModel.scale);
        
        // Set the final position and scale immediately
        cloudModel.position.set(0, 0.03, 0);
        cloudModel.rotation.set(0, 0, 0);
        cloudModel.scale.set(0.205, 0.205, 0.205);
    }
}, 
// Progress callback
(xhr) => {
    // Progress loading
},
// Error callback
(error) => {
    // Fallback to a simple sphere if loading fails
    const sphereGeometry = new THREE.SphereGeometry(0.3, 8, 6);
    const sphereMaterial = new THREE.MeshStandardMaterial({ 
        color: 0xFF6600,
        metalness: 0.0,
        roughness: 0.8,
        envMapIntensity: 1.0,
        transparent: false,
        opacity: 1.0,
        flatShading: true
    });
    cloudModel = new THREE.Mesh(sphereGeometry, sphereMaterial);
    cloudModel.scale.set(0.432, 0.432, 0.432);
    cloudModel.position.set(0, 0.05, 0);
    objectsParent.add(cloudModel);
    cloudModel.visible = false;
});

/**
 * Creates 12 small planes and attaches them to the dot parts of the cloud model
 */
function createDotPlanes(cloudModel) {
    // Check if planes already exist to prevent duplicates
    if (cloudModel.userData.dotPlanesCreated) {
        return;
    }
    
    // Clear cached arrays before populating
    cachedDotPlanes.length = 0;
    cachedConnectionBoxes.length = 0;

    // Create material for connection boxes
    const connectionMaterial = new THREE.MeshBasicMaterial({
        color: 0xffffff,
        transparent: true,
        opacity: 0.4
    });
    
    // Create a small plane geometry with 321:94 aspect ratio (250% size)
    const planeGeometry = new THREE.PlaneGeometry(0.8025, 0.235); // 321:94 aspect ratio scaled to 250%
    
    // Create texture loader for UI tag images
    const textureLoader = new THREE.TextureLoader();
    
    // Debug: Count dots by iterating through expected dot names instead of traversing
    let foundDots = 0;
    for (let i = 0; i < 12; i++) {
        const dotName = `dot${i}`;
        const dotPart = cloudModel.getObjectByName(dotName);
        if (dotPart) {
            foundDots++;
        }
    }
    
    // Create 12 planes and attach them to the dot parts
    let planesCreated = 0;
    for (let i = 0; i < 12; i++) {
        const dotName = `dot${i}`;
        
        // Find the dot part in the cloud model
        const dotPart = cloudModel.getObjectByName(dotName);
        
        if (dotPart) {
            // Load both ON and OFF textures for this tag
            const texturePathOFF = `/static/ui_tag${i}-OFF.png`;
            const texturePathON = `/static/ui_tag${i}-ON.png`;
            const textureOFF = configureTexture(textureLoader.load(texturePathOFF));
            const textureON = configureTexture(textureLoader.load(texturePathON));
            
            // Create a material for this specific plane with correct initial texture based on tagStates
            const initialTexture = tagStates[i] ? textureON : textureOFF;
            const planeMaterial = new THREE.MeshBasicMaterial({ 
                map: initialTexture, // Start with correct state based on tagStates
                transparent: true,
                opacity: 1.0,
                side: THREE.DoubleSide,
                alphaTest: 0.1, // Helps with transparency rendering
                depthTest: false,
                depthWrite: false,
                toneMapped: false
            });
            
            // Store both textures in the material for easy switching
            planeMaterial.userData = {
                textureOFF: textureOFF,
                textureON: textureON
            };
            
            // Create a plane mesh with the textured material
            const plane = new THREE.Mesh(planeGeometry, planeMaterial);
            
            // Position the plane slightly in front of the dot part
            plane.position.set(0, 0, 0.01);
            
            // Make sure the plane is visible
            plane.visible = true;
            plane.frustumCulled = false; // Disable frustum culling
            
            // Store reference to the plane for camera-facing updates
            plane.userData.isDotPlane = true;
            plane.userData.dotIndex = i; // Store the dot index for reference
            // Store texture references in userData for cleanup
            plane.userData.textureOFF = textureOFF;
            plane.userData.textureON = textureON;
            plane.userData.material = planeMaterial; // Store material reference for cleanup
            
            // Add the plane as a child of the dot part
            dotPart.add(plane);

            // Create a box for connection
            const boxGeometry = new THREE.BoxGeometry(1, 1, 1); // Will be scaled to connect points
            const connectionBox = new THREE.Mesh(boxGeometry, connectionMaterial);
            connectionBox.scale.set(0.015, 0.015, 1); // Initial scale - box thickness: 0.015 units
            
            // Store references for updating the connection
            connectionBox.userData.planeRef = plane;
            connectionBox.userData.lineIndex = i;
            cloudModel.add(connectionBox); // Add to cloud model to maintain proper transform hierarchy
            
            // Store connection reference in the plane for updates
            plane.userData.connectionBox = connectionBox;
            
            // Cache references for performance optimization
            cachedDotPlanes[i] = plane;
            cachedConnectionBoxes[i] = connectionBox;
            
            planesCreated++;
        } else {
        }
    }
    
    
    // Alternative approach: Create planes as direct children of cloud model for testing
    if (planesCreated === 0) {
        
        // Create a test plane directly in the cloud model with ON/OFF textures
        const textureOFF = configureTexture(textureLoader.load('/static/ui_tag0-OFF.png'));
        const textureON = configureTexture(textureLoader.load('/static/ui_tag0-ON.png'));
        const testMaterial = new THREE.MeshBasicMaterial({ 
            map: textureOFF, // Start with OFF state
            transparent: true,
            opacity: 1.0,
            side: THREE.DoubleSide,
            alphaTest: 0.1,
            depthTest: false,
            depthWrite: false,
            toneMapped: false
        });
        
        const testPlane = new THREE.Mesh(planeGeometry, testMaterial);
        testPlane.position.set(0, 0, 0.1); // Position it in front of the cloud
        testPlane.visible = true;
        testPlane.frustumCulled = false;
        testPlane.userData.isDotPlane = true;
        // Store texture references in userData for cleanup
        testPlane.userData.textureOFF = textureOFF;
        testPlane.userData.textureON = textureON;
        testPlane.userData.material = testMaterial; // Store material reference for cleanup
        cloudModel.add(testPlane);
        
    }
    
    // Mark that planes have been created to prevent duplicates
    cloudModel.userData.dotPlanesCreated = true;
    
}

/**
 * Function to adjust plane scaling parameters in real-time
 */
function updatePlaneScalingParameters(enabled, baseScale, minScale, maxScale, scaleRange) {
    planeScalingEnabled = enabled !== undefined ? enabled : planeScalingEnabled;
    planeBaseScale = baseScale !== undefined ? baseScale : planeBaseScale;
    planeMinScale = minScale !== undefined ? minScale : planeMinScale;
    planeMaxScale = maxScale !== undefined ? maxScale : planeMaxScale;
    planeScaleRange = scaleRange !== undefined ? scaleRange : planeScaleRange;
    
}

/**
 * Cleanup function to dispose of dot planes and their resources
 */
function cleanupDotPlanes(cloudModel) {
    if (!cloudModel.userData.dotPlanesCreated) {
        return;
    }
    
    // Clean up cached references
    for (let i = 0; i < cachedDotPlanes.length; i++) {
        const child = cachedDotPlanes[i];
        if (child && child.isMesh && child.userData.isDotPlane) {
            // Dispose of material and textures
            if (child.material) {
                child.material.dispose();
                if (child.material.userData.textureOFF) {
                    child.material.userData.textureOFF.dispose();
                }
                if (child.material.userData.textureON) {
                    child.material.userData.textureON.dispose();
                }
            }
            
            // Clean up connection box if it exists
            if (child.userData.connectionBox) {
                const box = child.userData.connectionBox;
                box.geometry.dispose();
                if (box.parent) {
                    box.parent.remove(box);
                }
            }

            // Remove the plane from its parent
            if (child.parent) {
                child.parent.remove(child);
            }
        }
    }
    
    // Clear cached arrays
    cachedDotPlanes.length = 0;
    cachedConnectionBoxes.length = 0;
    
    // Reset the flag
    cloudModel.userData.dotPlanesCreated = false;
}



/**
 * Makes all dot planes face the camera during rotation and scales them based on distance
 */
function updateDotPlanesToFaceCamera() {
    if (cloudModel && cloudModel.visible) {
        // Use cached references instead of traversing the entire model
        for (let i = 0; i < cachedDotPlanes.length; i++) {
            const child = cachedDotPlanes[i];
            if (!child || !child.userData.isDotPlane) continue;
            
            // Get the world position of the plane (needed for lookAt)
            const planeWorldPosition = new THREE.Vector3();
            child.getWorldPosition(planeWorldPosition);
            
            // Make the plane look at the camera
            child.lookAt(camera.position);
            
            // Dynamic scaling based on plane position relative to cloud center
            // Uses the plane's world Z position to determine front vs back
            if (planeScalingEnabled) {
                // Get the cloud's world position to use as reference
                const cloudWorldPosition = new THREE.Vector3();
                cloudModel.getWorldPosition(cloudWorldPosition);
                
                // Calculate plane position relative to cloud center
                const relativePosition = new THREE.Vector3();
                relativePosition.subVectors(planeWorldPosition, cloudWorldPosition);
                
                // Use Z component to determine front/back (assuming Z is depth)
                // Positive Z = front, Negative Z = back
                const zDepth = relativePosition.z;
                
                // Map Z depth to scale (0.3 to 2.0)
                // We'll use a small range since the cloud might not be very deep
                const zRange = 0.3; // Adjust this based on your cloud's actual depth
                const normalizedZ = Math.max(0, Math.min(1, (zDepth + zRange/2) / zRange));
                const scaleFactor = planeMinScale + (normalizedZ * (planeMaxScale - planeMinScale));
                
                // Apply the scale
                child.scale.set(scaleFactor, scaleFactor, scaleFactor);

                // Update connection box if it exists
                if (child.userData.connectionBox) {
                    const box = child.userData.connectionBox;
                    
                    // Get the dot part (parent of the plane)
                    const dotPart = child.parent;
                    if (!dotPart) return;
                    
                    // Get start (center) and end positions
                    const startPos = new THREE.Vector3(0, 0, 0);
                    const endPos = dotPart.position.clone();
                    
                    // Calculate direction and distance
                    const direction = endPos.clone().sub(startPos);
                    const distance = direction.length();
                    
                    if (distance > 0) {
                        // Position at midpoint
                        box.position.copy(startPos).add(direction.multiplyScalar(0.5));
                        
                        // Set scale - box length is along Y axis by default
                        box.scale.set(0.015, distance, 0.015);
                        
                        // Create rotation to align box with direction
                        const quaternion = new THREE.Quaternion();
                        const up = new THREE.Vector3(0, 1, 0);
                        quaternion.setFromUnitVectors(up, direction.normalize());
                        box.quaternion.copy(quaternion);
                    }
                }
                
                // Debug: log the scaling for all planes
            }
        }
    }
}

/**
 * Button Toggle Function with GSAP Scaling Transitions
 */
function toggleShoeCloudVisibility() {
    if (productModel && cloudModel) {
        if (productModel.visible) {
            // Switching from shoe to cloud
            currentObjectType = 'cloud';
            
            // Close the color picker when switching to cloud
            colorCircleOpen = false;
            gsap.to([colorCircleOrigin.scale, closeButtonPlane.scale], {
                x: 0,
                y: 0,
                z: 0,
                duration: 0.3,
                ease: "power2.inOut"
            });
            
            // Apply stored cloud rotation
            cloudModel.rotation.x = cloudRotationX;
            cloudModel.rotation.y = cloudRotationY;
            
            // Scale down header when switching to cloud
            gsap.to(header.scale, {
                x: 0.0,
                y: 0.0,
                z: 0.0,
                duration: 0.3,
                ease: "power2.inOut",
                onComplete: () => {
                    // Scale up header-tags when main header is scaled down
                    gsap.to(headerTags.scale, {
                        x: 1.0,
                        y: 1.0,
                        z: 1.0,
                        duration: 0.3,
                        ease: "power2.out"
                    });
                }
            });
            
            // Scale down ground plane when switching to cloud
            gsap.to(groundPlane.scale, {
                x: 0.0,
                y: 0.0,
                z: 0.0,
                duration: 0.3,
                ease: "power2.inOut",
                delay: 0.1
            });
            
            // Scale down shoe while scaling up cloud
            gsap.to(productModel.scale, {
                x: 0,
                y: 0,
                z: 0,
                duration: 0.3,
                ease: "power2.inOut",
                onComplete: () => {
                    productModel.visible = false;
                }
            });
            
            // Scale up cloud
            cloudModel.scale.set(0, 0, 0); // Start from 0 scale
            cloudModel.visible = true;
            
            // Make sure all planes are visible and properly scaled using cached references
            let planeCount = 0;
            for (let i = 0; i < cachedDotPlanes.length; i++) {
                const child = cachedDotPlanes[i];
                if (child && child.isMesh && child.material && child.material.color && child.material.color.getHex() === 0x00ff00) {
                    // This is one of our green planes - ensure it's visible
                    child.visible = true;
                    child.scale.set(1, 1, 1); // Reset plane scale to normal
                    child.frustumCulled = false; // Disable frustum culling
                    planeCount++;
                }
            }
            
            // If no planes found, try to create them again
            if (planeCount === 0) {
                createDotPlanes(cloudModel);
            }
            
            gsap.to(cloudModel.scale, {
                x: 0.205,
                y: 0.205,
                z: 0.205,
                duration: 0.3,
                ease: "power2.out",
                delay: 0.3 // Longer delay for more dramatic effect
            });
        } else {
            // Switching from cloud to shoe
            currentObjectType = 'shoe';
            
            // Apply stored shoe rotation
            productModel.rotation.y = shoeRotationY;
            
            // Scale down header-tags first
            gsap.to(headerTags.scale, {
                x: 0.0,
                y: 0.0,
                z: 0.0,
                duration: 0.3,
                ease: "power2.inOut",
                onComplete: () => {
                    // Scale header back up when switching back to shoe
                    gsap.to(header.scale, {
                        x: 1.0,
                        y: 1.0,
                        z: 1.0,
                        duration: 0.3,
                        ease: "power2.out"
                    });
                }
            });
            
            // Scale ground plane back to normal when switching back to shoe
            gsap.to(groundPlane.scale, {
                x: 1.0,
                y: 1.0,
                z: 1.0,
                duration: 0.3,
                ease: "power2.inOut",
                delay: 0.3
            });
            
            // Scale down cloud while scaling up shoe
            gsap.to(cloudModel.scale, {
                x: 0,
                y: 0,
                z: 0,
                duration: 0.3,
                ease: "power2.inOut",
                onComplete: () => {
                    cloudModel.visible = false;
                }
            });
            
            // Scale up shoe
            productModel.scale.set(0, 0, 0); // Start from 0 scale
            productModel.visible = true;
            
            // Update shoe color to match current color picker color
            if (colorButton.material.color) {
                updateShoeColor(colorButton.material.color);
            }
            
            const targetScale = sizeScales[selectedSizeIndex] || 1.0;
            gsap.to(productModel.scale, {
                x: targetScale,
                y: targetScale,
                z: targetScale,
                duration: 0.3,
                ease: "power2.out",
                delay: 0.3 // Longer delay for more dramatic effect
            });
        }
        
        // Update button texture after object switch
        updateButtonTexture();
    }
}

/**
 * HDRI Environment Setup
 */
const rgbeLoader = new RGBELoader();
rgbeLoader.load('/static/2k.hdr', function (texture) {
    // Prefilter the environment for PBR reflections
    const envRT = pmrem.fromEquirectangular(texture);
    scene.environment = envRT.texture;
    
    // Cleanup original HDR texture
    texture.dispose();
    
    // Background is managed per session; keep as-is here
});

/**
 * Enhanced Lighting System
 */
// Ambient light - reduced since we have HDRI environment
const ambientLight = new THREE.AmbientLight(0xffffff, 0.3);
scene.add(ambientLight);

// Key light (main directional light) - warm color
const keyLight = new THREE.DirectionalLight(0xfff4e6, 2.0);
keyLight.position.set(5, 10, 5);
keyLight.castShadow = false; // Disabled for XR/MR performance
scene.add(keyLight);

// Fill light - cooler color from opposite side
const fillLight = new THREE.DirectionalLight(0xe6f3ff, 1.2);
fillLight.position.set(-5, 8, -5);
scene.add(fillLight);

// Rim light - behind the object for edge definition
const rimLight = new THREE.DirectionalLight(0xffffff, 0.8);
rimLight.position.set(0, 5, -10);
scene.add(rimLight);

// Additional point light for more dynamic lighting
const pointLight = new THREE.PointLight(0xffffff, 1.5, 20);
pointLight.position.set(0, 6, 3);
pointLight.castShadow = false; // Disabled for XR/MR performance
scene.add(pointLight);

/**
 * Joint References
 */
const sphere = new THREE.SphereGeometry(0.002, 32, 32)
const defaultMat = new THREE.MeshBasicMaterial({ 
    color: 0xff0000, 
    opacity: 0,
    transparent: true 
})

const lThumbObj = new THREE.Mesh(sphere, defaultMat)
const lIndexObj = new THREE.Mesh(sphere, defaultMat)
const rThumbObj = new THREE.Mesh(sphere, defaultMat)
const rIndexObj = new THREE.Mesh(sphere, defaultMat)

scene.add(lThumbObj)
scene.add(lIndexObj)
scene.add(rThumbObj)
scene.add(rIndexObj)

/**
 * Pinch References
 */
const sphere2 = new THREE.SphereGeometry(0.006, 32, 32)
const defaultMat2 = new THREE.MeshLambertMaterial({ 
    color: 0xffffff,
})
const greenMat2 = new THREE.MeshLambertMaterial({ 
    color: 0x00ff00,
})

const lPinchSphere = new THREE.Mesh(sphere2, defaultMat2)
const rPinchSphere = new THREE.Mesh(sphere2, greenMat2)
scene.add(lPinchSphere)
scene.add(rPinchSphere)
lPinchSphere.material.transparent = true;
lPinchSphere.material.opacity = 0.0; // Completely transparent pinch reference
rPinchSphere.material.transparent = true;
rPinchSphere.material.opacity = 0.0; // Completely transparent pinch reference


/**
 * Hands
 */
var hand1, hand2;

hand1 = renderer.xr.getHand( 0 );
scene.add( hand1 );

hand2 = renderer.xr.getHand( 1 );
scene.add( hand2 );

const handsGroup = new THREE.Group();
handsGroup.add(hand1, hand2);
scene.add(handsGroup);
handsGroup.visible = false;

const clock = new THREE.Clock();

renderer.setAnimationLoop( function () {
    delta = clock.getDelta();
    gsap.ticker.tick(delta);

    renderer.render( scene, camera );
    
    
    
    handTracking();
    
    // Update dot planes to face camera during rotation
    updateDotPlanesToFaceCamera();
    
    // Only call positioning function if not already positioned
    if (!compositionPositioned) {
        positionCompositionAtHeadHeight();
    }
} );

function handTracking() {
    // Block interactions briefly after XR session starts to avoid auto-grab
    if (xrInteractionBlockedUntil && Date.now() < xrInteractionBlockedUntil) {
        return;
    }
    // Force hand1 to always be left hand tracking, hand2 to be right
    // This gives you consistent control regardless of WebXR's internal assignment
    const leftHand = hand1;
    const rightHand = hand2;
    
    // Left Hand Joint References (using hand1 consistently)
    if (leftHand && leftHand.joints && leftHand.joints['thumb-tip']) {
        lThumbObj.position.x = leftHand.joints['thumb-tip'].position.x;
        lThumbObj.position.y = leftHand.joints['thumb-tip'].position.y;
        lThumbObj.position.z = leftHand.joints['thumb-tip'].position.z;
        lThumbObj.rotation.x = leftHand.joints['thumb-tip'].rotation.x;
        lThumbObj.rotation.y = leftHand.joints['thumb-tip'].rotation.y;
        lThumbObj.rotation.z = leftHand.joints['thumb-tip'].rotation.z;
    }

    if (leftHand && leftHand.joints && leftHand.joints['index-finger-tip']) {
        lIndexObj.position.x = leftHand.joints['index-finger-tip'].position.x;
        lIndexObj.position.y = leftHand.joints['index-finger-tip'].position.y;
        lIndexObj.position.z = leftHand.joints['index-finger-tip'].position.z;
        lIndexObj.rotation.x = leftHand.joints['index-finger-tip'].rotation.x;
        lIndexObj.rotation.y = leftHand.joints['index-finger-tip'].rotation.y;
        lIndexObj.rotation.z = leftHand.joints['index-finger-tip'].rotation.z;
    }

    // Right Hand Joint References (using hand2 consistently)
    if (rightHand && rightHand.joints && rightHand.joints['thumb-tip']) {
        rThumbObj.position.x = rightHand.joints['thumb-tip'].position.x;
        rThumbObj.position.y = rightHand.joints['thumb-tip'].position.y;
        rThumbObj.position.z = rightHand.joints['thumb-tip'].position.z;
        rThumbObj.rotation.x = rightHand.joints['thumb-tip'].rotation.x;
        rThumbObj.rotation.y = rightHand.joints['thumb-tip'].rotation.y;
        rThumbObj.rotation.z = rightHand.joints['thumb-tip'].rotation.z;
    }

    if (rightHand && rightHand.joints && rightHand.joints['index-finger-tip']) {
        rIndexObj.position.x = rightHand.joints['index-finger-tip'].position.x;
        rIndexObj.position.y = rightHand.joints['index-finger-tip'].position.y;
        rIndexObj.position.z = rightHand.joints['index-finger-tip'].position.z;
        rIndexObj.rotation.x = rightHand.joints['index-finger-tip'].rotation.x;
        rIndexObj.rotation.y = rightHand.joints['index-finger-tip'].rotation.y;
        rIndexObj.rotation.z = rightHand.joints['index-finger-tip'].rotation.z;
    }

    // Left Hand Pinch Detection with hysteresis + priming
    const lTracked = isJointTracked(leftHand?.joints?.['thumb-tip']) && isJointTracked(leftHand?.joints?.['index-finger-tip']);
    const lIndexThumbDist = lTracked ? lIndexObj.position.distanceTo(lThumbObj.position) : Infinity;

    if (!lPinchOn && lIndexThumbDist < PINCH_START) lPinchOn = true;
    if (lPinchOn && lIndexThumbDist > PINCH_END) lPinchOn = false;
    if (lIndexThumbDist > PINCH_END) leftPinchPrimed = true;
    const leftActionablePinch = lTracked && leftPinchPrimed && lPinchOn;

    if (leftActionablePinch) {
        lPinchSphere.position.x = lThumbObj.position.x;
        lPinchSphere.position.y = lThumbObj.position.y;
        lPinchSphere.position.z = lThumbObj.position.z;
        
        // Copy thumb rotation to pinch reference
        lPinchSphere.rotation.x = lThumbObj.rotation.x;
        lPinchSphere.rotation.y = lThumbObj.rotation.y;
        lPinchSphere.rotation.z = lThumbObj.rotation.z;
        
        // Check if we can grab an object (only if color circle dot is not being dragged)
        if (!leftHandGrabbing && !rightHandGrabbing && !isAnimatingBack && !colorCircleDot.userData.isDragging) {
			// Only process grabbing when the shoe is visible
			if (productModel && productModel.visible) {
				// Get the world position of the cube for accurate distance calculation
				const cubeWorldPosition = new THREE.Vector3();
				productModel.getWorldPosition(cubeWorldPosition);
				const distanceToCube = lPinchSphere.position.distanceTo(cubeWorldPosition);
				if (distanceToCube < grabDistance) {
                // Start grabbing the cube
                leftHandGrabbing = true;
                grabbedObject = productModel;
                originalParent = productModel.parent;
                originalPosition = productModel.position.clone();
                originalRotation = productModel.rotation.clone();
                
                // Close color circle if it's open when grabbing starts
                if (colorCircleOpen) {
                    colorCircleOpen = false;
                    gsap.to([colorCircleOrigin.scale, closeButtonPlane.scale], {
                        x: 0,
                        y: 0,
                        z: 0,
                        duration: 0.3,
                        ease: "power2.inOut"
                    });
                }
                
                // Store the world rotation before parenting
                const worldQuaternion = productModel.getWorldQuaternion(new THREE.Quaternion());
                
                // Parent the cube to the pinch reference
                lPinchSphere.add(productModel);
                
                // Position the object with an offset to prevent hand intersection
                productModel.position.set(0, 0, -grabOffset); // Offset backward from pinch reference
                
                // Calculate and set the local rotation to maintain the original world rotation
                const parentWorldQuaternion = lPinchSphere.getWorldQuaternion(new THREE.Quaternion());
                const localQuaternion = worldQuaternion.clone().multiply(parentWorldQuaternion.invert());
                productModel.quaternion.copy(localQuaternion);
                userMovedShoeThisSession = true;
                
					// Left hand grabbed the cube
				}
			}
        }
        
        // If grabbing or color dot is being dragged, disable normal rotation
        if ((leftHandGrabbing && grabbedObject) || colorCircleDot.userData.isDragging) {
            leftHandRotationActive = false;
            // Object is either being grabbed or color selection is in progress
        } else if (!leftHandGrabbing) {
            // Normal rotation interaction when not grabbing and not selecting color
            if (!leftHandRotationActive) {
                leftHandRotationActive = true;
                lastLeftHandX = lThumbObj.position.x;
                lastLeftHandY = lThumbObj.position.y;
            }
            
            // Calculate movement for rotation
            if (leftHandRotationActive) {
                const currentLeftHandX = lThumbObj.position.x;
                const currentLeftHandY = lThumbObj.position.y;
                const deltaX = currentLeftHandX - lastLeftHandX;
                const deltaY = currentLeftHandY - lastLeftHandY;
                
                // Apply rotation to the currently visible object
				if (Math.abs(deltaX) > 0.001) {
					if (currentObjectType === 'shoe' && productModel && productModel.visible) {
                        shoeRotationY += deltaX * rotationSpeed;
                        productModel.rotation.y = shoeRotationY;
					} else if (currentObjectType === 'cloud' && cloudModel && cloudModel.visible) {
                        cloudRotationY += deltaX * rotationSpeed;
                        cloudModel.rotation.y = cloudRotationY;
                    }
                }
                
				// Apply Y-axis rotation for cloud only (inverted for intuitive control)
				if (Math.abs(deltaY) > 0.001 && currentObjectType === 'cloud' && cloudModel && cloudModel.visible) {
                    // Calculate the new rotation value
                    const newRotationX = cloudRotationX - deltaY * rotationSpeed; // Inverted for intuitive control
                    
                    // Limit the rotation between -60 and +60 degrees (in radians)
                    const minRotation = -Math.PI / 3; // -60 degrees
                    const maxRotation = Math.PI / 3;  // +60 degrees
                    
                    // Clamp the rotation value
                    cloudRotationX = Math.max(minRotation, Math.min(maxRotation, newRotationX));
                    cloudModel.rotation.x = cloudRotationX;
                }
                
                lastLeftHandX = currentLeftHandX;
                lastLeftHandY = currentLeftHandY;
            }
        }
    } else {
        
        // Release grabbed object if left hand stops pinching
        if (leftHandGrabbing && grabbedObject) {
            leftHandGrabbing = false;
            
            if (SNAP_BACK_ON_RELEASE) {
                if (!isAnimatingBack) {
                    isAnimatingBack = true;
                    // Get the current world position before reparenting
                    const currentWorldPosition = new THREE.Vector3();
                    grabbedObject.getWorldPosition(currentWorldPosition);
                    // Restore to original parent
                    originalParent.add(grabbedObject);
                    // Convert world -> local within originalParent
                    const localPosition = new THREE.Vector3();
                    localPosition.copy(currentWorldPosition);
                    originalParent.worldToLocal(localPosition);
                    // Set starting local position
                    grabbedObject.position.copy(localPosition);
                    // Animate back with elastic
                    gsap.to(grabbedObject.position, {
                        x: originalPosition.x,
                        y: originalPosition.y,
                        z: originalPosition.z,
                        duration: 1.0,
                        ease: "elastic.out(0.7, 0.5)",
                        onComplete: () => {
                            isAnimatingBack = false;
                        }
                    });
                    gsap.to(grabbedObject.rotation, {
                        x: originalRotation.x,
                        y: originalRotation.y,
                        z: originalRotation.z,
                        duration: 1.0,
                        ease: "elastic.out(0.7, 0.5)"
                    });
                }
            } else {
                dropGrabbedObjectInPlace(grabbedObject, objectsParent);
            }
            
            
            // Reset grab state
            grabbedObject = null;
            originalParent = null;
            originalPosition = null;
            originalRotation = null;
            
            // Left hand released the cube
        }
        
        // Stop chair rotation when left hand stops pinching
        if (leftHandRotationActive) {
            leftHandRotationActive = false;
        }
    }

    // Button touch detection for both hands
    const currentTime = Date.now();
    
    // Get button's world position (accounting for hierarchy)
    const buttonWorldPosition = new THREE.Vector3();
    button.getWorldPosition(buttonWorldPosition);
    
    // Check if left index finger is touching the button
    if (lIndexObj && button) {
        const leftIndexToButton = lIndexObj.position.distanceTo(buttonWorldPosition);
        if (leftIndexToButton < 0.06 && !buttonTouchActive && (currentTime - lastButtonTouchTime) > buttonTouchCooldown) {
            buttonTouchActive = true;
            lastButtonTouchTime = currentTime;
            animateButtonPress(); // Button press animation handles texture change
        }
    }
    
    // Check if right index finger is touching the button
    if (rIndexObj && button) {
        const rightIndexToButton = rIndexObj.position.distanceTo(buttonWorldPosition);
        if (rightIndexToButton < 0.06 && !buttonTouchActive && (currentTime - lastButtonTouchTime) > buttonTouchCooldown) {
            buttonTouchActive = false;
            lastButtonTouchTime = currentTime;
            animateButtonPress(); // Button press animation handles texture change
        }
    }

    // Color Button Press Detection
    const colorButtonWorldPosition = new THREE.Vector3();
    colorButton.getWorldPosition(colorButtonWorldPosition);

    // Check both index fingers for color button interaction
    const leftIndexToColorButton = lIndexObj ? lIndexObj.position.distanceTo(colorButtonWorldPosition) : Infinity;
    const rightIndexToColorButton = rIndexObj ? rIndexObj.position.distanceTo(colorButtonWorldPosition) : Infinity;

    // Check if either index finger is touching the color button
    if ((leftIndexToColorButton < 0.06 || rightIndexToColorButton < 0.06) && 
        (currentTime - lastButtonTouchTime) > buttonTouchCooldown) {
        
        // Toggle color circle state
        colorCircleOpen = !colorCircleOpen;
        lastButtonTouchTime = currentTime;

        // Animate the color button press
        gsap.to(colorButton.scale, {
            x: 0.7,
            y: 0.7,
            z: 0.7,
            duration: 0.1,
            ease: "power2.out",
            onComplete: () => {
                gsap.to(colorButton.scale, {
                    x: 1,
                    y: 1,
                    z: 1,
                    duration: 0.2,
                    ease: "power2.out"
                });
            }
        });

        // Scale the colorCircleOrigin and closeButtonPlane based on state
        gsap.to(colorCircleOrigin.scale, {
            x: colorCircleOpen ? 1 : 0,
            y: colorCircleOpen ? 1 : 0,
            z: colorCircleOpen ? 1 : 0,
            duration: 0.3,
            ease: "power2.inOut"
        });
        
        gsap.to(closeButtonPlane.scale, {
            x: colorCircleOpen ? 1 : 0,
            y: colorCircleOpen ? 1 : 0,
            z: colorCircleOpen ? 1 : 0,
            duration: 0.3,
            ease: "power2.inOut"
        });
    }

    // Color Circle Dot interaction (only when circle is open)
    if (colorCircleOpen) {
        // Get world positions for calculations
        const dotWorldPosition = new THREE.Vector3();
        colorCircleDot.getWorldPosition(dotWorldPosition);
        
        const centerWorldPosition = new THREE.Vector3();
        colorCircleCenter.getWorldPosition(centerWorldPosition);

        // Check both hands for pinching near the dot
        const leftPinchingNearDot = lPinchOn && lPinchSphere.position.distanceTo(dotWorldPosition) < 0.06;
        const rightPinchingNearDot = rPinchOn && rPinchSphere.position.distanceTo(dotWorldPosition) < 0.06;

        // Start dragging if either hand pinches near the dot
        if (!colorCircleDot.userData.isDragging) {
            if (leftPinchingNearDot) {
                colorCircleDot.userData.isDragging = true;
                colorCircleDot.userData.activeHand = 'left';
            } else if (rightPinchingNearDot) {
                colorCircleDot.userData.isDragging = true;
                colorCircleDot.userData.activeHand = 'right';
            }
        }

        // Handle dragging
        if (colorCircleDot.userData.isDragging) {
            // Get the active pinch position based on which hand started the drag
            const pinchPosition = colorCircleDot.userData.activeHand === 'left' ? 
                lPinchSphere.position : rPinchSphere.position;
            
            // Check if the active hand is still pinching
            const isStillPinching = colorCircleDot.userData.activeHand === 'left' ? 
                lPinchOn : rPinchOn;

            // Convert pinch position to local space relative to the center
            const localPinchPosition = new THREE.Vector3();
            localPinchPosition.copy(pinchPosition);
            colorCircleCenter.worldToLocal(localPinchPosition);

            // Calculate angle from center to pinch position (in XY plane of the colorCirclePlane)
            const angle = Math.atan2(localPinchPosition.y, localPinchPosition.x);

            // Position the dot at the stored radius and calculated angle
            const radius = colorCircleDot.userData.radius;
            colorCircleDot.position.x = Math.cos(angle) * radius;
            colorCircleDot.position.y = Math.sin(angle) * radius;
            colorCircleDot.position.z = 0; // Keep it in the plane

            // Convert angle to hue (0-360 degrees)
            // Adjust angle to start from red (0 degrees) at the right side
            let hue = ((Math.atan2(colorCircleDot.position.y, colorCircleDot.position.x) * 180 / Math.PI) + 360) % 360;
            
            // Create HSL color with full saturation and 50% lightness
            const color = new THREE.Color();
            color.setHSL(hue / 360, 1, 0.5);
            
            // Update both dot and color button colors
            colorCircleDot.material.color = color;
            colorButton.material.color = color;
            
            // Update shoe color layer with the same color
            updateShoeColor(color);

            // Stop dragging if the active hand releases pinch
            if (!isStillPinching) {
                colorCircleDot.userData.isDragging = false;
                colorCircleDot.userData.activeHand = null;
            }
        }
    }
    
    // Reset button touch state when finger moves away
    if (lIndexObj && button && rIndexObj && button) {
        const leftIndexToButton = lIndexObj.position.distanceTo(buttonWorldPosition);
        const rightIndexToButton = rIndexObj.position.distanceTo(buttonWorldPosition);
        
        if (leftIndexToButton > 0.09 && rightIndexToButton > 0.09) { // Reset threshold adjusted to 0.09
            buttonTouchActive = false;
        }
    }

    // Size button interaction detection
    if (sizeTexts && sizeTexts.length > 0 && typeof animateSizeButtonPress === 'function') {
        sizeTexts.forEach((sizeButton, index) => {
            if (!sizeButton || !sizeButton.userData) return;
            
            try {
                // Get size button's world position
                const sizeButtonWorldPosition = new THREE.Vector3();
                sizeButton.getWorldPosition(sizeButtonWorldPosition);

                // Check both index fingers for interaction
                const leftIndexDistance = lIndexObj ? lIndexObj.position.distanceTo(sizeButtonWorldPosition) : Infinity;
                const rightIndexDistance = rIndexObj ? rIndexObj.position.distanceTo(sizeButtonWorldPosition) : Infinity;

                // Interaction threshold
                const touchThreshold = 0.06; // Adjust this value based on testing

                // Check if either index finger is touching the size button
                const isTouching = leftIndexDistance < touchThreshold || rightIndexDistance < touchThreshold;
                
                // Only trigger on initial touch and respect cooldown
                // Also prevent interaction when color circle is open
                if (isTouching && !sizeButton.userData.isPressed && 
                    !buttonTouchActive && 
                    !colorCircleOpen &&
                    (currentTime - lastButtonTouchTime) > buttonTouchCooldown) {
                    
                    // Animate button press and change selection
                    animateSizeButtonPress(index);
                    
                    // Update cooldown
                    lastButtonTouchTime = currentTime;
                    buttonTouchActive = true;
                } else if (!isTouching && sizeButton.userData.isPressed) {
                    // Reset press state when finger moves away
                    sizeButton.userData.isPressed = false;
                }
            } catch (error) {
            }
        });
    }

    // Tag interaction detection
    if (cloudModel && cloudModel.visible) {
        // Use cached references instead of traversing the entire model
        for (let i = 0; i < cachedDotPlanes.length; i++) {
            const child = cachedDotPlanes[i];
            if (!child || !child.userData.isDotPlane) continue;
            
            const tagIndex = child.userData.dotIndex;
            if (tagIndex === undefined) continue;

            // Get tag's world position
            const tagWorldPosition = new THREE.Vector3();
            child.getWorldPosition(tagWorldPosition);

            // Check both index fingers for interaction
            const leftIndexDistance = lIndexObj ? lIndexObj.position.distanceTo(tagWorldPosition) : Infinity;
            const rightIndexDistance = rIndexObj ? rIndexObj.position.distanceTo(tagWorldPosition) : Infinity;

            // Interaction threshold
            const touchThreshold = 0.05; // Adjust this value based on testing

            // Check if either index finger is touching the tag
            const isTouching = leftIndexDistance < touchThreshold || rightIndexDistance < touchThreshold;
            
            // Only trigger on initial touch and respect cooldown
            if (isTouching && !child.userData.isPressed && 
                !buttonTouchActive && 
                (currentTime - lastButtonTouchTime) > buttonTouchCooldown) {
                
                child.userData.isPressed = true;
                
                // Toggle tag state
                tagStates[tagIndex] = !tagStates[tagIndex];
                
                // Update texture based on new state
                const material = child.material;
                if (tagStates[tagIndex]) {
                    material.map = material.userData.textureON;
                } else {
                    material.map = material.userData.textureOFF;
                }
                material.needsUpdate = true;

                // Kill any existing animations on this tag
                gsap.killTweensOf(child.scale);

                // Temporarily disable dynamic scaling during touch animation
                const wasScalingEnabled = planeScalingEnabled;
                planeScalingEnabled = false;

                // Store original scale for reference
                const originalScale = child.scale.x;
                
                // Add touch feedback with smoother animation
                gsap.to(child.scale, {
                    x: originalScale * 0.7,
                    y: originalScale * 0.7,
                    z: originalScale * 0.7,
                    duration: 0.12,
                    ease: "power2.out",
                    onComplete: () => {
                        // Start scale up animation
                        gsap.to(child.scale, {
                            x: originalScale,
                            y: originalScale,
                            z: originalScale,
                            duration: 0.3,
                            ease: "power2.out",
                            onComplete: () => {
                                // Re-enable dynamic scaling after animation
                                planeScalingEnabled = wasScalingEnabled;
                            }
                        });
                    }
                });

                // Update cooldown
                lastButtonTouchTime = currentTime;
                buttonTouchActive = true;
            } else if (!isTouching && child.userData.isPressed) {
                // Reset press state when finger moves away
                child.userData.isPressed = false;
            }
        }
    }
    
    // Right Hand Pinch Detection with hysteresis + priming
    const rTracked = isJointTracked(rightHand?.joints?.['thumb-tip']) && isJointTracked(rightHand?.joints?.['index-finger-tip']);
    const rIndexThumbDist = rTracked ? rIndexObj.position.distanceTo(rThumbObj.position) : Infinity;

    if (!rPinchOn && rIndexThumbDist < PINCH_START) rPinchOn = true;
    if (rPinchOn && rIndexThumbDist > PINCH_END) rPinchOn = false;
    if (rIndexThumbDist > PINCH_END) rightPinchPrimed = true;
    const rightActionablePinch = rTracked && rightPinchPrimed && rPinchOn;

    // Always update the pinch reference sphere position when pinching
    if (rightActionablePinch) {
        rPinchSphere.position.x = rThumbObj.position.x;
        rPinchSphere.position.y = rThumbObj.position.y;
        rPinchSphere.position.z = rThumbObj.position.z;
        
        // Copy thumb rotation to pinch reference
        rPinchSphere.rotation.x = rThumbObj.rotation.x;
        rPinchSphere.rotation.y = rThumbObj.rotation.y;
        rPinchSphere.rotation.z = rThumbObj.rotation.z;
        
		// Check if we can grab an object (only if color circle dot is not being dragged)
		if (!leftHandGrabbing && !rightHandGrabbing && !isAnimatingBack && !colorCircleDot.userData.isDragging) {
            // Get the world position of the cube for accurate distance calculation
            const cubeWorldPosition = new THREE.Vector3();
            productModel.getWorldPosition(cubeWorldPosition);
            const distanceToCube = rPinchSphere.position.distanceTo(cubeWorldPosition);
            if (distanceToCube < grabDistance) {
                // Start grabbing the cube
                rightHandGrabbing = true;
                grabbedObject = productModel;
                originalParent = productModel.parent;
                originalPosition = productModel.position.clone();
                originalRotation = productModel.rotation.clone();
                
                // Close color circle if it's open when grabbing starts
                if (colorCircleOpen) {
                    colorCircleOpen = false;
                    gsap.to([colorCircleOrigin.scale, closeButtonPlane.scale], {
                        x: 0,
                        y: 0,
                        z: 0,
                        duration: 0.3,
                        ease: "power2.inOut"
                    });
                }
                
                // Store the world rotation before parenting
                const worldQuaternion = productModel.getWorldQuaternion(new THREE.Quaternion());
                
                // Parent the cube to the pinch reference
                rPinchSphere.add(productModel);
                
                // Position the object with an offset to prevent hand intersection
                productModel.position.set(0, 0, -grabOffset); // Offset backward from pinch reference
                
                // Calculate and set the local rotation to maintain the original world rotation
                const parentWorldQuaternion = rPinchSphere.getWorldQuaternion(new THREE.Quaternion());
                const localQuaternion = worldQuaternion.clone().multiply(parentWorldQuaternion.invert());
                productModel.quaternion.copy(localQuaternion);
                userMovedShoeThisSession = true;
                
				// Right hand grabbed the cube
			}
        }
        
        // If grabbing or color dot is being dragged, disable normal rotation
        if ((rightHandGrabbing && grabbedObject) || colorCircleDot.userData.isDragging) {
            rightHandRotationActive = false;
            // Object is either being grabbed or color selection is in progress
        } else if (!rightHandGrabbing) {
            // Normal rotation interaction when not grabbing and not selecting color
            if (!rightHandRotationActive) {
                rightHandRotationActive = true;
                lastRightHandX = rThumbObj.position.x;
                lastRightHandY = rThumbObj.position.y;
            }
            
            // Calculate movement for rotation
            if (rightHandRotationActive) {
                const currentRightHandX = rThumbObj.position.x;
                const currentRightHandY = rThumbObj.position.y;
                const deltaX = currentRightHandX - lastRightHandX;
                const deltaY = currentRightHandY - lastRightHandY;
                
                // Apply rotation to the currently visible object
				if (Math.abs(deltaX) > 0.001) {
					if (currentObjectType === 'shoe' && productModel && productModel.visible) {
                        shoeRotationY += deltaX * rotationSpeed;
                        productModel.rotation.y = shoeRotationY;
					} else if (currentObjectType === 'cloud' && cloudModel && cloudModel.visible) {
                        cloudRotationY += deltaX * rotationSpeed;
                        cloudModel.rotation.y = cloudRotationY;
                    }
                }
                
				// Apply Y-axis rotation for cloud only (inverted for intuitive control)
				if (Math.abs(deltaY) > 0.001 && currentObjectType === 'cloud' && cloudModel && cloudModel.visible) {
                    // Calculate the new rotation value
                    const newRotationX = cloudRotationX - deltaY * rotationSpeed; // Inverted for intuitive control
                    
                    // Limit the rotation between -60 and +60 degrees (in radians)
                    const minRotation = -Math.PI / 3; // -60 degrees
                    const maxRotation = Math.PI / 3;  // +60 degrees
                    
                    // Clamp the rotation value
                    cloudRotationX = Math.max(minRotation, Math.min(maxRotation, newRotationX));
                    cloudModel.rotation.x = cloudRotationX;
                }
                
                lastRightHandX = currentRightHandX;
                lastRightHandY = currentRightHandY;
            }
        }
    } else {
        
        // Release grabbed object if right hand stops pinching
        if (rightHandGrabbing && grabbedObject) {
            rightHandGrabbing = false;
            
            if (SNAP_BACK_ON_RELEASE) {
                if (!isAnimatingBack) {
                    isAnimatingBack = true;
                    // Get the current world position before reparenting
                    const currentWorldPosition = new THREE.Vector3();
                    grabbedObject.getWorldPosition(currentWorldPosition);
                    // Restore to original parent
                    originalParent.add(grabbedObject);
                    // Convert world -> local within originalParent
                    const localPosition = new THREE.Vector3();
                    localPosition.copy(currentWorldPosition);
                    originalParent.worldToLocal(localPosition);
                    // Set starting local position
                    grabbedObject.position.copy(localPosition);
                    // Animate back with elastic
                    gsap.to(grabbedObject.position, {
                        x: originalPosition.x,
                        y: originalPosition.y,
                        z: originalPosition.z,
                        duration: 1.0,
                        ease: "elastic.out(0.7, 0.5)",
                        onComplete: () => {
                            isAnimatingBack = false;
                        }
                    });
                    gsap.to(grabbedObject.rotation, {
                        x: originalRotation.x,
                        y: originalRotation.y,
                        z: originalRotation.z,
                        duration: 1.0,
                        ease: "elastic.out(0.7, 0.5)"
                    });
                }
            } else {
                dropGrabbedObjectInPlace(grabbedObject, objectsParent);
            }
            
            // Reset grab state
            grabbedObject = null;
            originalParent = null;
            originalPosition = null;
            originalRotation = null;
            
            // Right hand released the cube
        }
        
        // Stop chair rotation when right hand stops pinching
        if (rightHandRotationActive) {
            rightHandRotationActive = false;
        }
    }
}

